import PropTypes from 'prop-types';

/**
 * Reusable RadioGroup component
 * - Simple radio look (no pill background)
 * - When selected: radio background is green, inner dot is black, and border is black
 * - Accessible native <input type="radio" /> with a custom visual via Tailwind peer classes
 *
 * Props:
 *  - name: string (required) — all radios share this name
 *  - options: Array<{ value: string|number, label: React.ReactNode, disabled?: boolean }>
 *  - value: string|number — currently selected value (controlled)
 *  - onChange: (event) => void — called with the native change event
 *  - onBlur: (event) => void — forwarded to inputs (useful for Formik)
 *  - direction: "row" | "col" — layout direction
 *  - className: string — extra classes for the wrapper
 *  - size: "sm" | "md" | "lg" — control radio size & text size
 *  - required, disabled: boolean
 */
export default function RadioGroup({
    name,
    options = [],
    value,
    onChange,
    onBlur,
    direction = 'row',
    className = '',
    size = 'md',
    required = false,
    disabled = false,
    ...rest
}) {
    const dir =
        direction === 'col' ? 'flex-col gap-2' : 'flex-row gap-3 flex-wrap';
    const dims =
        size === 'sm'
            ? { box: 'h-4 w-4', svg: 'h-3 w-3', text: 'text-sm' }
            : size === 'lg'
              ? { box: 'h-6 w-6', svg: 'h-5 w-5', text: 'text-base' }
              : { box: 'h-5 w-5', svg: 'h-4 w-4', text: 'text-sm' };

    return (
        <div role='radiogroup' className={`flex ${dir} ${className}`} {...rest}>
            {options.map(opt => {
                const checked = String(value) === String(opt.value);
                const id = `${name}-${String(opt.value)}`;
                const isDisabled = disabled || opt.disabled;
                return (
                    <label
                        key={id}
                        htmlFor={id}
                        className={`flex items-center mr-4 mb-2 select-none ${dims.text} ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        {/* Visually hidden native input, but keep it a RADIO so only one can be active */}
                        <input
                            id={id}
                            type='radio'
                            name={name}
                            value={opt.value}
                            checked={checked}
                            onChange={onChange}
                            onBlur={onBlur}
                            required={required}
                            disabled={isDisabled}
                            className='appearance-none outline-none opacity-0 absolute h-4 w-4 peer'
                        />

                        {/* Rounded box that looks like the provided checkbox, but styles like a radio when selected */}
                        <div
                            className={`border rounded-full w-6 h-6 flex flex-shrink-0 justify-center items-center mr-2 transition-colors ${
                                checked
                                    ? 'bg-[#52FF00] border-black'
                                    : 'bg-white border-black'
                            }`}
                        >
                            {/* Good icon (check) shown when selected; must be BLACK */}
                            <svg
                                className={`${checked ? 'block' : 'hidden'} ${dims.svg} pointer-events-none`}
                                viewBox='0 0 17 12'
                                xmlns='http://www.w3.org/2000/svg'
                                aria-hidden
                            >
                                <path
                                    d='m25.576 11.414c0.56558 0.55188 0.56558 1.4439 0 1.9961l-9.404 9.176c-0.28213 0.27529-0.65247 0.41385-1.0228 0.41385-0.37034 0-0.74068-0.13855-1.0228-0.41385l-4.7019-4.588c-0.56584-0.55188-0.56584-1.4442 0-1.9961 0.56558-0.55214 1.4798-0.55214 2.0456 0l3.679 3.5899 8.3812-8.1779c0.56558-0.55214 1.4798-0.55214 2.0456 0z'
                                    transform='translate(-9 -11)'
                                    fill='black'
                                />
                            </svg>
                        </div>

                        <div>
                            <span>{opt.label ?? String(opt.value)}</span>
                            {opt.errorLabel ? (
                                <p className='text-red-500 text-xs italic py-1'>
                                    {opt.errorLabel}
                                </p>
                            ) : null}
                        </div>
                    </label>
                );
            })}
        </div>
    );
}

RadioGroup.propTypes = {
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            label: PropTypes.node,
            errorLabel: PropTypes.node,
            disabled: PropTypes.bool
        })
    ).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    direction: PropTypes.oneOf(['row', 'col']),
    className: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    required: PropTypes.bool,
    disabled: PropTypes.bool
};
